import React, { useState } from 'react';
import Footer from 'components/Footer';
import PoiTypePill from 'components/pills/PoiTypePill';
import DestinationCategory from 'components/destinations/DestinationCategory';
import AreaCard from 'components/destinations/AreaCard';
import { dehydrate, QueryClient, useQuery } from 'react-query';
import { fetchDestinationBySlug } from 'data/fetchers';
import DestinationGallery from 'components/destinations/DestinationGallery';
import SearchResults from 'components/search/SearchResults';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import parse from 'html-react-parser';
import { stripHtmlTags } from 'content/helpers';

export async function getStaticPaths() {
    return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps({ params }) {
    if (!params?.slug) return { notFound: true };

    const queryClient = new QueryClient();

    try {
        await queryClient.fetchQuery(['destination', params.slug], () => fetchDestinationBySlug(params.slug));
    } catch (error) {
        return {
            notFound: true
        };
    }

    return {
        props: {
            dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
            slug: params.slug
        },
        revalidate: 60
    };
}

const DestinationPage = ({ slug }: { slug: string }) => {
    const {
        data: { data: destination }
    } = useQuery(['destination', slug], fetchDestinationBySlug);

    const [showFullDescription, setShowFullDescription] = useState(false);
    const showDescription = () => {
        setShowFullDescription(!showFullDescription);
    };

    return (
        <>
            <NextSeo
                title={destination.name}
                description={stripHtmlTags(destination.description)}
                openGraph={{
                    images: [
                        {
                            url:
                                destination.area.featured_image?.conversions?.facebook?.url ??
                                destination.area.featured_image?.original?.url
                        }
                    ]
                }}
            />

            <div className="single-page destination">
                <div>
                    <div className="container relative flex flex-col mt-8 lg:flex-row">
                        <div className="lg:basis-2/5 lg:pr-6">
                            <h1 className="text-3xl md:text-5xl mb-2">{destination.name}</h1>
                            {destination.area?.parent && (
                                <Link
                                    href={`/destinacija/${destination.area.parent.slug}`}
                                    className="mb-8 underline font-bold "
                                >
                                    {destination.area.parent.name}
                                </Link>
                            )}

                            {destination.description && (
                                <div
                                    className={`mt-6 lg:text-base text-sm overflow-hidden text-ellipsis ${
                                        showFullDescription ? 'line-clamp-none' : 'line-clamp-6'
                                    }`}
                                    style={{ blockOverflow: 'ellipsis' }}
                                >
                                    {parse(destination.description)}
                                </div>
                            )}

                            {destination.description?.length > 100 && (
                                <button
                                    className="my-4 text font-bold cursor-pointer text-brand"
                                    onClick={showDescription}
                                >
                                    {showFullDescription ? 'Preberi manj' : 'Preberi več'}
                                </button>
                            )}
                        </div>
                        <div className="lg:basis-3/5 mt-8 md:mt-0">
                            {destination.featured_photos.length > 0 && <DestinationGallery destination={destination} />}
                        </div>
                    </div>
                    {destination.poi_types.length > 0 && (
                        <ul className="flex flex-row gap-4 py-12 px-6 flex-nowrap whitespace-nowrap overflow-x-auto">
                            {destination.poi_types.map((poi_type) => {
                                return (
                                    <li key={poi_type.id} className="first:ml-auto last:mr-auto">
                                        <PoiTypePill poiType={poi_type} destination={destination} />
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {destination.popular_areas.length > 0 && (
                        <div className="flex flex-col items-center justify-center md:px-0 py-16 container">
                            <h1 className="text-2xl text-center md:text-3xl lg:mb-1">
                                Priljubljene destinacije v območju {destination.area.name}
                            </h1>

                            <div className="flex justify-center flex-wrap text-center gap-8 mt-8 md:gap-12">
                                {destination.popular_areas.map((area) => {
                                    return <AreaCard key={area.id} area={area} />;
                                })}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-24 mt-12">
                        {destination.categories.map((category) => {
                            return (
                                <DestinationCategory key={category.id} category={category} destination={destination} />
                            );
                        })}
                    </div>

                    {destination.pois.length > 0 && (
                        <div className="py-16 container">
                            <h2 className="text-brand text-center mb-8">
                                Izletne ideje v destinaciji {destination.name}
                            </h2>
                            <SearchResults pois={destination.pois} />
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-center justify-center md:px-0 py-12 mb-24 container">
                    <h1 className="text-2xl text-center md:text-3xl lg:mb-1">Ostale destinacije v bližini</h1>

                    <div className="flex flex-wrap justify-center text-center gap-8 md:gap-12 mt-8">
                        {destination.nearby_areas.map((area) => {
                            return <AreaCard key={area.id} area={area} />;
                        })}
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};

export default DestinationPage;
